import React from 'react';
import { Helmet } from 'react-helmet';
import {
  SITE_NAME,
  SITE_ORIGIN,
  DEFAULT_OG_IMAGE,
  absoluteUrl,
} from '../../data/site';

/**
 * Shared blog SEO / GEO head tags — Open Graph, Twitter, canonical, JSON-LD.
 * See docs/BLOG_PAGE.md § SEO & GEO
 */
export function BlogIndexSeo({ posts }) {
  const url = absoluteUrl('/blog');
  const title = 'Construx Blog — Field notes on ADUs, precision framing & housing reform';
  const description =
    'Product, field, and market notes from Construx: backyard ADUs, kit-of-parts envelopes, local build partners, and guided reads of housing supply legislation.';

  const itemList = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    '@id': `${url}#blog`,
    name: title,
    description,
    url,
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_ORIGIN,
      logo: {
        '@type': 'ImageObject',
        url: DEFAULT_OG_IMAGE,
      },
    },
    blogPost: (posts || []).map((post) => ({
      '@type': 'BlogPosting',
      headline: post.title,
      url: absoluteUrl(`/blog/${post.slug}`),
      datePublished: post.date,
      description: post.lede,
    })),
  };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="keywords"
        content="Construx blog, ADU, accessory dwelling unit, housing reform, precision framing, kit of parts, Central Texas builders, backyard office"
      />
      <link rel="canonical" href={url} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />

      <script type="application/ld+json">{JSON.stringify(itemList)}</script>
    </Helmet>
  );
}

export function BlogPostSeo({ post }) {
  const path = `/blog/${post.slug}`;
  const url = absoluteUrl(path);
  const title = post.seoTitle || `${post.title} — Construx Blog`;
  const description = post.seoDescription || post.lede;
  const image = post.cover?.startsWith('http')
    ? post.cover
    : absoluteUrl(post.cover || '/logo512.png');
  const keywords =
    post.keywords ||
    `Construx, ${post.category}, ADU, housing, ${post.title}`;

  const article = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${url}#article`,
    headline: post.title,
    description,
    image: [image],
    datePublished: post.date,
    dateModified: post.dateModified || post.date,
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_ORIGIN,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_ORIGIN,
      logo: {
        '@type': 'ImageObject',
        url: DEFAULT_OG_IMAGE,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    articleSection: post.category,
    keywords,
    isAccessibleForFree: true,
    inLanguage: 'en-US',
  };

  if (post.citation) {
    article.citation = post.citation;
  }
  if (post.about) {
    article.about = post.about;
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={SITE_NAME} />
      <link rel="canonical" href={url} />

      <meta property="og:type" content="article" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_US" />
      <meta property="article:published_time" content={post.date} />
      <meta property="article:section" content={post.category} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <script type="application/ld+json">{JSON.stringify(article)}</script>
    </Helmet>
  );
}
